import { useState, useEffect, useCallback } from 'react';
import { ref, get, update, child } from 'firebase/database';
import { database } from '../firebase';
import { LOAN_TYPE_NAMES, LOAN_TYPES } from '../forms/loanTypes';

import { FaEdit, FaFolderOpen, FaCheckCircle, FaRupeeSign, FaTimesCircle, FaBuilding, FaCar, FaHome, FaFileContract, FaUser, FaBriefcase, FaMoneyBillWave, FaTractor, FaBullseye, FaChartLine } from 'react-icons/fa';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Area, AreaChart } from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#14B8A6'];

const AdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        totalLoans: 0,
        totalCustomers: 0,
        totalInsurance: 0,
        totalEmployees: 0,
        mostCasesReferredBy: [],
        recentLoans: [],
        monthlyCommission: {
            totalGet: 0,
            totalGiven: 0
        },
        rejectedCount: 0,
        vehicleSubCounts: { newCar: 0, usedCar: 0, newCommercial: 0, usedCommercial: 0 },
        homeSubCounts: {
            constructionBusiness: 0, constructionSalaried: 0,
            flatPurchaseBusiness: 0, flatPurchaseSalaried: 0,
            btBusiness: 0, btSalaried: 0, commercialPurchase: 0
        },
        lapSubCounts: { lapBusiness: 0, lapGavthan: 0 },
        businessSubCounts: { business: 0, project: 0, machinery: 0 },
        agriSubCounts: { agriculture: 0, animal: 0 },
        financeSchemeCount: 0,
        loanCounts: {
            carLoan: 0,
            commercialLoan: 0,
            homeLoan: 0,
            lapLoan: 0,
            personalLoan: 0,
            businessLoan: 0,
            workingCapitalLoan: 0,
            agricultureLoan: 0
        },
        totalDisbursedAmount: 0,
        chartData: {
            statusData: [],
            loanTypeData: [],
            dailyTrendData: []
        },

        totalTarget: 0,
        achievedAmount: 0
    });
    const [showTargetModal, setShowTargetModal] = useState(false);
    const [targetMode, setTargetMode] = useState('monthly'); // 'monthly' | 'yearly'
    const [viewMode, setViewMode] = useState('month'); // 'month' | 'year'
    const [newTarget, setNewTarget] = useState('');
    const [allCustomers, setAllCustomers] = useState([]);

    // Initialize with Local Date (YYYY-MM-DD) correctly
    const getTodayString = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getStartOfMonthString = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}-01`;
    };

    const [fromDate, setFromDate] = useState(getStartOfMonthString());
    const [toDate, setToDate] = useState(getTodayString());

    const [masterData, setMasterData] = useState({
        customers: [],
        employees: [],
        dealers: [],
        loans: [],
        insurance: [],
        target: 0
    });
    const [initialFetchDone, setInitialFetchDone] = useState(false);

    const [activeFilter, setActiveFilter] = useState(null); // 'daily', 'login', 'sanctioned', 'disbursed'
    const [subTypeFilter, setSubTypeFilter] = useState(null); // 'newCar', 'usedCar', 'newCommercial', 'usedCommercial'

    const fetchAllData = useCallback(async () => {
        try {
            const [
                customersData,
                employeesData,
                dealersData,
                loansData,
                insuranceData,
                targetData
            ] = await Promise.all([
                loadCustomersData(),
                loadEmployeesData(),
                loadDealersData(),
                loadLoansData(),
                loadInsuranceData(),
                loadTargetData()
            ]);

            setMasterData({
                customers: customersData,
                employees: employeesData,
                dealers: dealersData,
                loans: loansData,
                insurance: insuranceData,
                target: targetData
            });
            setAllCustomers(customersData);
            setInitialFetchDone(true);
            return {
                customers: customersData,
                employees: employeesData,
                dealers: dealersData,
                loans: loansData,
                insurance: insuranceData,
                target: targetData
            };
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            return null;
        }
    }, []);

    const loadDashboardData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setLoading(true);

        // Use data from masterData if already fetched, or fetch it now
        let currentData = masterData;
        if (!initialFetchDone || isRefresh) {
            const fetched = await fetchAllData();
            if (fetched) currentData = fetched;
        }

        if (!currentData.customers) return;

        try {
            const { customers: customersData, employees: employeesData, dealers: dealersData, loans: loansData, insurance: insuranceData, target: targetData } = currentData;

            // Process the data
            const processedData = processAllData(customersData, employeesData, dealersData, loansData, insuranceData, fromDate, toDate);

            // STRATEGY: Treat DB value as YEARLY TARGET
            const yearlyTarget = Number(targetData) || 25000000;
            const monthlyBase = yearlyTarget / 12; // base monthly target (e.g. 25L if yearly = 3Cr)

            const currentYear = new Date().getFullYear();
            const currentMonthIndex = new Date().getMonth(); // 0=Jan … 11=Dec

            // ── Rolling carryover logic ───────────────────────────────────────────
            // For each completed month (Jan → month before current):
            //   monthTarget = monthlyBase + carryover (shortfall from prev month)
            //   disbursed   = sum of loanDisbursementDate in that month
            //   surplus     = disbursed - monthTarget  (can be negative = shortfall)
            //   carryover   = -surplus  (shortfall becomes positive carryover; surplus reduces next target)
            //
            // Example: base=25L, Jan disbursed=30L → surplus=5L → carryover=-5L
            //          Feb target = 25L + (-5L) = 20L
            //          Feb disbursed=15L → shortfall=5L → carryover=5L
            //          Mar target = 25L + 5L = 30L
            // ─────────────────────────────────────────────────────────────────────

            let carryover = 0; // positive = shortfall carried forward; negative = surplus reduces next target

            // Helper: get disbursed amount for a calendar month using loanDisbursementDate
            const getMonthDisbursed = (year, monthIndex) => {
                const monthStart = new Date(year, monthIndex, 1);
                monthStart.setHours(0, 0, 0, 0);
                const monthEnd = new Date(year, monthIndex + 1, 0);
                monthEnd.setHours(23, 59, 59, 999);

                return customersData.reduce((sum, c) => {
                    if (!c.loanDisbursementDate) return sum;
                    const d = new Date(c.loanDisbursementDate);
                    if (d >= monthStart && d <= monthEnd) {
                        const amt = Number(c.financeScheme?.actualDisbursement) || Number(c.loanAmount) || 0;
                        return sum + amt;
                    }
                    return sum;
                }, 0);
            };

            // Calculate carryover from Jan up to (but not including) current month
            // Only count shortfall/surplus for months that had actual disbursement records
            // (avoids phantom shortfall from months before loanDisbursementDate was tracked)
            for (let i = 0; i < currentMonthIndex; i++) {
                const disbursed = getMonthDisbursed(currentYear, i);
                // Skip months with zero disbursement data — treat as "no tracking" not "shortfall"
                if (disbursed === 0) continue;
                const thisMonthTarget = monthlyBase + carryover;
                const surplus = disbursed - thisMonthTarget; // positive = over-achieved
                carryover = -surplus; // positive carryover = shortfall; negative = surplus (reduces next month)
            }

            // Current month's effective target
            const currentMonthTarget = Math.max(0, monthlyBase + carryover);

            // Current month's disbursed (so far)
            const currentMonthDisbursed = getMonthDisbursed(currentYear, currentMonthIndex);

            // Year-to-date disbursed (all months Jan → today)
            const startOfYear = new Date(currentYear, 0, 1);
            startOfYear.setHours(0, 0, 0, 0);
            const now = new Date();
            now.setHours(23, 59, 59, 999);

            const totalDisbursedYTD = customersData.reduce((sum, c) => {
                if (!c.loanDisbursementDate) return sum;
                const d = new Date(c.loanDisbursementDate);
                if (d >= startOfYear && d <= now) {
                    return sum + (Number(c.financeScheme?.actualDisbursement) || Number(c.loanAmount) || 0);
                }
                return sum;
            }, 0);

            processedData.yearlyTarget = yearlyTarget;
            processedData.monthlyTarget = currentMonthTarget;
            processedData.monthlyBase = monthlyBase;
            processedData.carryover = carryover; // positive=shortfall, negative=surplus
            processedData.yearToDateDisbursed = totalDisbursedYTD;
            processedData.achievedAmount = currentMonthDisbursed;

            setDashboardData(prev => ({
                ...prev,
                ...processedData,
                yearlyTarget,
                monthlyTarget: currentMonthTarget,
                monthlyBase,
                carryover,
                yearToDateDisbursed: totalDisbursedYTD,
                achievedAmount: currentMonthDisbursed
            }));
        } catch (error) {
            console.error('Error processing dashboard data:', error);
        } finally {
            if (isRefresh || !initialFetchDone) setLoading(false);
        }
    }, [fromDate, toDate, initialFetchDone, masterData, fetchAllData]);

    useEffect(() => {
        if (!initialFetchDone) {
            loadDashboardData(true);
        } else {
            loadDashboardData(false);
        }
    }, [fromDate, toDate]); // Re-run when dates change, but without full loading screen after initial load

    const loadCustomersData = async () => {
        try {
            if (!database) return [];

            const customersSnap = await get(ref(database, 'customers'));
            if (!customersSnap.exists()) return [];

            const customersData = customersSnap.val();
            const customers = Object.keys(customersData).map(customerId => {
                const data = customersData[customerId];
                const basicInfo = data.customer_details?.basic_info || {};

                const dateStr = data.createdAt || data.created_at || data.timestamp;
                let parsedDate = dateStr ? new Date(dateStr) : new Date();
                if (isNaN(parsedDate.getTime())) parsedDate = new Date();

                // Only use explicitly stored loanDisbursementDate — never fall back to login date
                const loanDisbursementDate = data.loanDisbursementDate ||
                    data.loan_application?.loan_details?.loanDisbursementDate || null;

                return {
                    id: data.customerId || customerId,
                    name: basicInfo.full_name || data.fullName || data.name || '',
                    loanType: data.loanType ||
                        data.loan_application?.loan_details?.loan_type ||
                        basicInfo.loan_type ||
                        data.loan_type || '',
                    loanCategory: data.loanCategory ||
                        data.loan_application?.loan_details?.loan_category ||
                        data.loan_category || '',
                    loanSubcategory: data.loanSubcategory ||
                        data.loan_application?.loan_details?.loan_subcategory ||
                        data.loan_subcategory || '',
                    createdAt: parsedDate,
                    referedBy: basicInfo.refered_by ||
                        basicInfo.referred_by ||
                        basicInfo.referedby ||
                        basicInfo.referredby ||
                        data.referedBy ||
                        data.referredBy ||
                        data.referedby ||
                        data.referredby || '',
                    status: data.status || data.applicationStatus ||
                        data.loan_application?.loan_details?.status || '',
                    loanAmount: data.loanAmount || 0,
                    branch: data.branch || '',
                    commission: data.commission || null,
                    financeScheme: data.financeScheme || null,
                    remark: data.remark ||
                        data.loan_application?.loan_details?.remark ||
                        data.notes || '',
                    loanDisbursementDate
                };
            });

            return customers;
        } catch (error) {
            console.error('Error loading customers:', error);
            return [];
        }
    };

    const loadEmployeesData = async () => {
        try {
            if (!database) return [];

            const employeesRef = ref(database, 'employees');
            const snapshot = await get(employeesRef);

            if (!snapshot.exists()) return [];

            const employeesData = snapshot.val();
            const employees = Object.keys(employeesData).map(empId => ({
                id: empId,
                name: employeesData[empId].name || '',
                isActive: employeesData[empId].isActive !== undefined ? employeesData[empId].isActive : true
            }));

            return employees;
        } catch (error) {
            console.error('Error loading employees:', error);
            return [];
        }
    };

    const loadDealersData = async () => {
        try {
            if (!database) return [];

            const dealersRef = ref(database, 'dealers');
            const snapshot = await get(dealersRef);

            if (!snapshot.exists()) return [];

            const dealersData = snapshot.val();
            const dealers = Object.keys(dealersData).map(dealerId => ({
                id: dealerId,
                name: dealersData[dealerId].dealerName || '',
                commission: dealersData[dealerId].commission || 0,
                isActive: dealersData[dealerId].isActive !== undefined ? dealersData[dealerId].isActive : true
            }));

            return dealers;
        } catch (error) {
            console.error('Error loading dealers:', error);
            return [];
        }
    };

    const loadLoansData = async () => {
        try {
            if (!database) return [];

            const loansRef = ref(database, 'loans');
            const snapshot = await get(loansRef);

            if (!snapshot.exists()) return [];

            const loansData = snapshot.val();
            const loans = Object.keys(loansData).map(loanId => {
                const data = loansData[loanId];
                const dateStr = data.createdAt || data.created_at || data.timestamp;
                let parsedDate = dateStr ? new Date(dateStr) : new Date();
                if (isNaN(parsedDate.getTime())) parsedDate = new Date();
                
                return {
                    id: loanId,
                    customerId: data.customerId || '',
                    branch: data.branch || '',
                    createdAt: parsedDate,
                    loanAmount: data.loanAmount || 0,
                    loanType: data.loanType || ''
                };
            });

            return loans;
        } catch (error) {
            console.error('Error loading loans:', error);
            return [];
        }
    };

    const loadInsuranceData = async () => {
        try {
            if (!database) return [];

            const insuranceRef = ref(database, 'insurences');
            const snapshot = await get(insuranceRef);

            if (!snapshot.exists()) return [];

            const insuranceData = snapshot.val();
            const insurance = Object.keys(insuranceData).map(insId => {
                const data = insuranceData[insId];
                const dateStr = data.createdAt || data.created_at || data.timestamp;
                let parsedDate = dateStr ? new Date(dateStr) : new Date();
                if (isNaN(parsedDate.getTime())) parsedDate = new Date();

                return {
                    id: insId,
                    customerName: data.customerName || '',
                    createdAt: parsedDate
                };
            });

            return insurance;
        } catch (error) {
            console.error('Error loading insurance:', error);
            return [];
        }
    };
    const loadTargetData = async () => {
        try {
            const dbRef = ref(database);
            const snapshot = await get(child(dbRef, 'settings/target'));
            return snapshot.val() || 0;
        } catch (error) {
            console.error('Error loading target:', error);
            return 0;
        }
    };

    const handleUpdateTarget = async () => {
        try {
            if (!newTarget) return;
            const updates = {};

            // Store EXACTLY what the user entered (Yearly Target)
            const yearlyValue = Number(newTarget);

            if (yearlyValue < 0) {
                alert('Target cannot be negative');
                return;
            }

            // Saving as yearlyTarget
            updates['customers/targetSettings/yearlyTarget'] = yearlyValue;
            updates['settings/target'] = yearlyValue;

            await update(ref(database), updates);

            // Trigger reload to recalculate everything
            loadDashboardData(true);
            setShowTargetModal(false);
            setNewTarget('');
        } catch (error) {
            console.error('Error updating target:', error);
            alert('Failed to update target');
        }
    };




    const processAllData = (customers, employees, dealers, loans, insurance, startDate, endDate) => {
        // Date Logic - ensure rigorous comparison
        const startOfTargetDate = new Date(startDate);
        startOfTargetDate.setHours(0, 0, 0, 0);

        const endOfTargetDate = new Date(endDate);
        endOfTargetDate.setHours(23, 59, 59, 999);

        // Filter customers strictly by createdAt date
        const dateFilteredCustomers = customers.filter(c => {
            if (!c.createdAt) return false;
            const created = new Date(c.createdAt);
            // created is likely UTC if from DB string 'Z', but JS Date compares timestamps correctly
            return created >= startOfTargetDate && created <= endOfTargetDate;
        });

        const dateFilteredLoans = loans.filter(l => {
            if (!l.createdAt) return false;
            const created = new Date(l.createdAt);
            return created >= startOfTargetDate && created <= endOfTargetDate;
        });

        const dateFilteredInsurance = insurance.filter(i => {
            if (!i.createdAt) return false;
            const created = new Date(i.createdAt);
            return created >= startOfTargetDate && created <= endOfTargetDate;
        });

        // Calculate total loans for the date range
        const totalLoans = dateFilteredLoans.length > 0 ? dateFilteredLoans.length : dateFilteredCustomers.length;

        // 1. Daily Leads: Count of files created on that date
        const dailyLeads = dateFilteredCustomers.length;

        // 2. Login: Active processing (Created on that date, status is processing)
        const loginCount = dateFilteredCustomers.filter(c => {
            const s = (c.status || '').toLowerCase();
            const isNew = s === 'new lead';
            const isSanctioned = s.includes('sanction') || s.includes('approval');
            const isDisbursed = s.includes('disburs') || s.includes('payout');
            const isRejected = s.includes('reject');

            return !isNew && !isSanctioned && !isDisbursed && !isRejected;
        }).length;

        // 3. Sanctioned: Count of Sanctioned (Created on that date)
        const sanctionedCount = dateFilteredCustomers.filter(c =>
            c.status?.toLowerCase().includes('sanction') ||
            c.status?.toLowerCase().includes('approval')
        ).length;

        // 4. Disbursed: Count customers whose loanDisbursementDate falls in the selected date range
        const disbursedInRange = customers.filter(c => {
            if (!c.loanDisbursementDate) return false;
            const d = new Date(c.loanDisbursementDate);
            return d >= startOfTargetDate && d <= endOfTargetDate;
        });
        const disbursedCount = disbursedInRange.length;

        // Total disbursed amount (financeScheme.actualDisbursement first, fallback to loanAmount)
        const totalDisbursedAmount = disbursedInRange.reduce((sum, c) => {
            const amt = Number(c.financeScheme?.actualDisbursement) || Number(c.loanAmount) || 0;
            return sum + amt;
        }, 0);

        // 5. Rejected: Count of Rejected (Created on that date)
        const rejectedCount = dateFilteredCustomers.filter(c =>
            c.status?.toLowerCase().includes('reject')
        ).length;

        // 6. Finance Scheme: Count of Finance Schemes created on that date
        // Use financeScheme.createdAt if available, otherwise fallback to customer.createdAt
        const financeSchemeCount = customers.filter(c => {
            if (!c.financeScheme) return false;
            const schemeCreated = c.financeScheme.createdAt ? new Date(c.financeScheme.createdAt) : new Date(c.createdAt);
            return schemeCreated >= startOfTargetDate && schemeCreated < endOfTargetDate;
        }).length;

        // 7. Calculate Specific Loan Type Counts (Requests)
        const loanCounts = {
            carLoan: 0,
            commercialLoan: 0,
            homeLoan: 0,
            lapLoan: 0,
            personalLoan: 0,
            businessLoan: 0,
            workingCapitalLoan: 0,
            agricultureLoan: 0
        };

        const vehicleSubCounts = {
            newCar: 0,
            usedCar: 0,
            newCommercial: 0,
            usedCommercial: 0
        };

        const homeSubCounts = {
            constructionBusiness: 0,
            constructionSalaried: 0,
            flatPurchaseBusiness: 0,
            flatPurchaseSalaried: 0,
            btBusiness: 0,
            btSalaried: 0,
            commercialPurchase: 0
        };

        const lapSubCounts = {
            lapBusiness: 0,
            lapGavthan: 0
        };

        const businessSubCounts = {
            business: 0,
            project: 0,
            machinery: 0
        };

        const agriSubCounts = {
            agriculture: 0,
            animal: 0
        };

        dateFilteredCustomers.forEach(c => {
            const type = c.loanType; // Use raw value for comparison with LOAN_TYPES
            const subType = c.loanSubcategory; // Check subcategory as well

            // Vehicle Loan (Car + Commercial) - Match Keys OR Display Names in Type OR SubType OR Category
            if (
                c.loanCategory === 'vehicle_loan' ||
                type === LOAN_TYPES.NEW_CAR || type === LOAN_TYPE_NAMES[LOAN_TYPES.NEW_CAR] ||
                subType === LOAN_TYPES.NEW_CAR ||
                type === LOAN_TYPES.USED_CAR || type === LOAN_TYPE_NAMES[LOAN_TYPES.USED_CAR] ||
                subType === LOAN_TYPES.USED_CAR ||
                type === LOAN_TYPES.RTO_CAR || type === LOAN_TYPE_NAMES[LOAN_TYPES.RTO_CAR] ||
                subType === LOAN_TYPES.RTO_CAR ||
                type === LOAN_TYPES.NEW_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.NEW_COMMERCIAL] ||
                subType === LOAN_TYPES.NEW_COMMERCIAL ||
                type === LOAN_TYPES.USED_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.USED_COMMERCIAL] ||
                subType === LOAN_TYPES.USED_COMMERCIAL ||
                type === LOAN_TYPES.RTO_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.RTO_COMMERCIAL] ||
                subType === LOAN_TYPES.RTO_COMMERCIAL
            ) {
                loanCounts.carLoan++;

                // Subtype Counts
                if (type === LOAN_TYPES.NEW_CAR || subType === LOAN_TYPES.NEW_CAR || type === LOAN_TYPE_NAMES[LOAN_TYPES.NEW_CAR]) {
                    vehicleSubCounts.newCar++;
                } else if (type === LOAN_TYPES.USED_CAR || subType === LOAN_TYPES.USED_CAR || type === LOAN_TYPE_NAMES[LOAN_TYPES.USED_CAR]) {
                    vehicleSubCounts.usedCar++;
                } else if (type === LOAN_TYPES.NEW_COMMERCIAL || subType === LOAN_TYPES.NEW_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.NEW_COMMERCIAL]) {
                    vehicleSubCounts.newCommercial++;
                } else if (type === LOAN_TYPES.USED_COMMERCIAL || subType === LOAN_TYPES.USED_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.USED_COMMERCIAL]) {
                    vehicleSubCounts.usedCommercial++;
                }
            }
            // Commercial Vehicle Loan (New, Used, RTO Commercial)
            else if (
                type === LOAN_TYPES.NEW_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.NEW_COMMERCIAL] ||
                subType === LOAN_TYPES.NEW_COMMERCIAL ||
                type === LOAN_TYPES.USED_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.USED_COMMERCIAL] ||
                subType === LOAN_TYPES.USED_COMMERCIAL ||
                type === LOAN_TYPES.RTO_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.RTO_COMMERCIAL] ||
                subType === LOAN_TYPES.RTO_COMMERCIAL
            ) {
                loanCounts.commercialLoan++;
            }
            // Home Loan
            else if (
                type === LOAN_TYPES.HOME_LOAN_CONSTRUCTION_BUSINESS ||
                type === LOAN_TYPES.HOME_LOAN_CONSTRUCTION_SALARIED ||
                type === LOAN_TYPES.FLAT_PURCHASE_SALARIED ||
                type === LOAN_TYPES.FLAT_PURCHASE_BUSINESS ||
                type === LOAN_TYPES.HOME_LOAN_BT_BUSINESS ||
                type === LOAN_TYPES.HOME_LOAN_BT_SALARY ||
                type === LOAN_TYPES.COMMERCIAL_PURCHASE ||
                type === LOAN_TYPES.HOME_LOAN
            ) {
                loanCounts.homeLoan++;

                if (type === LOAN_TYPES.HOME_LOAN_CONSTRUCTION_BUSINESS) homeSubCounts.constructionBusiness++;
                else if (type === LOAN_TYPES.HOME_LOAN_CONSTRUCTION_SALARIED) homeSubCounts.constructionSalaried++;
                else if (type === LOAN_TYPES.FLAT_PURCHASE_BUSINESS) homeSubCounts.flatPurchaseBusiness++;
                else if (type === LOAN_TYPES.FLAT_PURCHASE_SALARIED) homeSubCounts.flatPurchaseSalaried++;
                else if (type === LOAN_TYPES.HOME_LOAN_BT_BUSINESS) homeSubCounts.btBusiness++;
                else if (type === LOAN_TYPES.HOME_LOAN_BT_SALARY) homeSubCounts.btSalaried++;
                else if (type === LOAN_TYPES.COMMERCIAL_PURCHASE) homeSubCounts.commercialPurchase++;
            }
            // LAP / Mortgage
            else if (type === LOAN_TYPES.LAP_BUSINESS || type === LOAN_TYPES.LAP_GAVTHAN) {
                loanCounts.lapLoan++;
                if (type === LOAN_TYPES.LAP_BUSINESS) lapSubCounts.lapBusiness++;
                else if (type === LOAN_TYPES.LAP_GAVTHAN) lapSubCounts.lapGavthan++;
            }
            // Personal Loan
            else if (type === LOAN_TYPES.PERSONAL_LOAN) {
                loanCounts.personalLoan++;
            }
            // Business Loan
            else if (type === LOAN_TYPES.BUSINESS_LOAN || type === LOAN_TYPES.PROJECT_LOAN || type === LOAN_TYPES.MACHINERY_LOAN) {
                loanCounts.businessLoan++;
                if (type === LOAN_TYPES.BUSINESS_LOAN) businessSubCounts.business++;
                else if (type === LOAN_TYPES.PROJECT_LOAN) businessSubCounts.project++;
                else if (type === LOAN_TYPES.MACHINERY_LOAN) businessSubCounts.machinery++;
            }
            // Working Capital
            else if (type === LOAN_TYPES.WORKING_CAPITAL) {
                loanCounts.workingCapitalLoan++;
            }
            // Agriculture Loan
            else if (type === LOAN_TYPES.AGRICULTURE_LOAN || type === LOAN_TYPES.ANIMAL_LOAN) {
                loanCounts.agricultureLoan++;
                if (type === LOAN_TYPES.AGRICULTURE_LOAN) agriSubCounts.agriculture++;
                else if (type === LOAN_TYPES.ANIMAL_LOAN) agriSubCounts.animal++;
            }
        });


        // Keep only real referral data - no fake fallbacks

        // Recent loans (top 5 most recent) - use loans data if available
        let recentLoans = [];
        if (dateFilteredLoans.length > 0) {
            console.log('🔍 Debug Loans Matching:', {
                loanCount: dateFilteredLoans.length,
                firstLoanCustId: dateFilteredLoans[0]?.customerId,
                firstCustomerIds: dateFilteredCustomers.slice(0, 3).map(c => c.id)
            });

            // Use loans data and match with customer names
            recentLoans = dateFilteredLoans
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .slice(0, 5)
                .map((loan, index) => {
                    // Find customer name by matching customerId (ensure string comparison)
                    const customer = customers.find(c => String(c.id) === String(loan.customerId));
                    if (!customer) console.warn(`⚠️ Customer not found for loan`, loan);
                    return {
                        srNo: index + 1,
                        customerName: customer ? customer.name : 'Unknown Customer',
                        loanType: LOAN_TYPE_NAMES[loan.loanType] || loan.loanType || 'Not Specified',
                        createdAt: loan.createdAt,
                        branch: loan.branch || ''
                    };
                });
        } else {
            // ... assigned above
            recentLoans = dateFilteredCustomers
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .slice(0, 5)
                .map((customer, index) => ({
                    srNo: index + 1,
                    customerName: customer.name,
                    loanType: LOAN_TYPE_NAMES[customer.loanType] || customer.loanType || 'Not Specified',
                    createdAt: customer.createdAt,
                    branch: customer.branch || ''
                }));
        }

        // Keep only real loan data - no fake fallbacks

        // Monthly commission calculation based on actual customer commission data
        let totalGet = 0;
        let totalGiven = 0;

        // Calculate from customer commission records
        dateFilteredCustomers.forEach(customer => {
            if (customer.commission) {
                totalGet += Number(customer.commission.payoutAmount) || 0;
                totalGiven += Number(customer.commission.balanceAmount) || 0;
            }
        });

        let monthlyCommission = {
            totalGet: totalGet,
            totalGiven: totalGiven
        };

        // Keep only real commission data - no fake fallbacks

        // Most cases referred by dealers - count referrals from customers/loans
        const referralCounts = {};
        const dataForReferrals = dateFilteredCustomers.length > 0 ? dateFilteredCustomers : dateFilteredLoans;

        console.log('🔍 Debug Referrals:', {
            sourceLength: dataForReferrals.length,
            sampleItems: dataForReferrals.slice(0, 3).map(i => ({ id: i.id, referedBy: i.referedBy, referedby: i.referedby }))
        });

        dataForReferrals.forEach(item => {
            // Check both referedBy and referedby (case inconsistency)
            const referrer = item.referedBy || item.referedby || '';
            if (referrer && referrer.trim()) {
                const referrerName = referrer.trim();
                referralCounts[referrerName] = (referralCounts[referrerName] || 0) + 1;
            }
        });

        // Get top referrers (dealers) with percentages
        const mostCasesReferredBy = Object.entries(referralCounts)
            .map(([name, count]) => ({
                name,
                count,
                percentage: totalLoans > 0 ? ((count / totalLoans) * 100).toFixed(1) : 0
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);

        // Calculate Revenue by Status
        const sanctionedAmount = dateFilteredCustomers
            .filter(c => c.status?.toLowerCase().includes('sanction') || c.status?.toLowerCase().includes('approval'))
            .reduce((sum, c) => sum + (Number(c.loanAmount) || 0), 0);

        const disbursedAmount = dateFilteredCustomers
            .filter(c => c.status?.toLowerCase().includes('disburs') || c.status?.toLowerCase().includes('payout'))
            .reduce((sum, c) => sum + (Number(c.loanAmount) || 0), 0);

        const processingAmount = dateFilteredCustomers
            .filter(c => {
                const s = (c.status || '').toLowerCase();
                const isNew = s === 'new lead';
                const isSanctioned = s.includes('sanction') || s.includes('approval');
                const isDisbursed = s.includes('disburs') || s.includes('payout');
                const isRejected = s.includes('reject');
                return !isNew && !isSanctioned && !isDisbursed && !isRejected;
            })
            .reduce((sum, c) => sum + (Number(c.loanAmount) || 0), 0);

        // Calculate Monthly Trends (last 6 months)
        const monthlyTrends = (() => {
            const trends = {};
            const now = new Date();

            // Generate last 6 months
            for (let i = 5; i >= 0; i--) {
                const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                const monthName = date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
                trends[monthKey] = {
                    month: monthName,
                    applications: 0,
                    sanctioned: 0,
                    disbursed: 0
                };
            }

            // Populate with customer data
            customers.forEach(c => {
                const createdDate = new Date(c.createdAt);
                const monthKey = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;

                if (trends[monthKey]) {
                    trends[monthKey].applications++;
                    if (c.status?.toLowerCase().includes('sanction') || c.status?.toLowerCase().includes('approval')) {
                        trends[monthKey].sanctioned++;
                    }
                    if (c.status?.toLowerCase().includes('disburs') || c.status?.toLowerCase().includes('payout')) {
                        trends[monthKey].disbursed++;
                    }
                }
            });

            return Object.values(trends);
        })();

        return {
            totalLoans,
            totalCustomers: dateFilteredCustomers.length,
            totalInsurance: dateFilteredInsurance.length,
            dailyLeads,
            loginCount,
            sanctionedCount,
            disbursedCount,
            totalDisbursedAmount,
            rejectedCount,
            mostCasesReferredBy,
            recentLoans,
            monthlyCommission,
            financeSchemeCount,
            loanCounts,
            vehicleSubCounts,
            homeSubCounts,
            lapSubCounts,
            businessSubCounts,
            agriSubCounts,
            chartData: {
                statusData: [
                    { name: 'New Leads', value: dailyLeads, color: '#3B82F6' },
                    { name: 'In Process', value: loginCount, color: '#F59E0B' },
                    { name: 'Sanctioned', value: sanctionedCount, color: '#10B981' },
                    { name: 'Disbursed', value: disbursedCount, color: '#8B5CF6' },
                    { name: 'Rejected', value: rejectedCount, color: '#EF4444' }
                ],
                funnelData: [
                    { name: 'Leads', value: dailyLeads, fill: '#3B82F6' },
                    { name: 'Processing', value: loginCount, fill: '#F59E0B' },
                    { name: 'Sanctioned', value: sanctionedCount, fill: '#10B981' },
                    { name: 'Disbursed', value: disbursedCount, fill: '#8B5CF6' }
                ],
                revenueData: [
                    { name: 'Processing', amount: processingAmount, fill: '#F59E0B' },
                    { name: 'Sanctioned', amount: sanctionedAmount, fill: '#10B981' },
                    { name: 'Disbursed', amount: disbursedAmount, fill: '#8B5CF6' }
                ].filter(item => item.amount > 0),
                monthlyTrends: monthlyTrends,
                loanTypeData: [
                    { name: 'Vehicle', value: loanCounts.carLoan + loanCounts.commercialLoan },
                    { name: 'Home', value: loanCounts.homeLoan },
                    { name: 'LAP', value: loanCounts.lapLoan },
                    { name: 'Personal', value: loanCounts.personalLoan },
                    { name: 'Business', value: loanCounts.businessLoan },
                    { name: 'Other', value: loanCounts.workingCapitalLoan + loanCounts.agricultureLoan }
                ].filter(item => item.value > 0),
                dailyTrendData: (() => {
                    const trendMap = {};
                    const start = new Date(startDate);
                    const end = new Date(endDate);

                    // Normalize times
                    const current = new Date(start);
                    current.setHours(0, 0, 0, 0);
                    const endLimit = new Date(end);
                    endLimit.setHours(23, 59, 59, 999);

                    // Format function to guarantee YYYY-MM-DD
                    const formatYMD = (d) => {
                        const dt = new Date(d);
                        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
                    };

                    // Fill all days in range
                    while (current <= endLimit) {
                        const dateStr = formatYMD(current); // YYYY-MM-DD
                        trendMap[dateStr] = {
                            date: dateStr,
                            shortDate: current.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
                            leads: 0,
                            processing: 0,
                            sanctioned: 0,
                            rejected: 0,
                            disbursed: 0
                        };
                        current.setDate(current.getDate() + 1);
                    }

                    // Populate with actual data
                    dateFilteredCustomers.forEach(c => {
                        const dateStr = formatYMD(c.createdAt);
                        if (trendMap[dateStr]) {
                            trendMap[dateStr].leads++;
                            const s = (c.status || '').toLowerCase();
                            const isSanctioned = s.includes('sanction') || s.includes('approval');
                            const isDisbursed = s.includes('disburs') || s.includes('payout');
                            const isRejected = s.includes('reject');
                            const isProcessing = !s.includes('new lead') && !isSanctioned && !isDisbursed && !isRejected;

                            if (isSanctioned) trendMap[dateStr].sanctioned++;
                            if (isDisbursed) trendMap[dateStr].disbursed++;
                            if (isRejected) trendMap[dateStr].rejected++;
                            if (isProcessing) trendMap[dateStr].processing++;
                        }
                    });
                    return Object.values(trendMap).sort((a, b) => new Date(a.date) - new Date(b.date));
                })()
            },

            achievedAmount: 0,
            totalTarget: 0
        };
    };


    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getFilteredCustomers = () => {
        // Debug Logging
        console.log('🔍 getFilteredCustomers Run:', {
            activeFilter,
            totalCustomers: allCustomers.length,
            fromDate,
            toDate
        });

        if (!activeFilter) return [];

        // Same filtering logic as dashboard stats
        const startOfTargetDate = new Date(fromDate);
        startOfTargetDate.setHours(0, 0, 0, 0);

        const endOfTargetDate = new Date(toDate);
        endOfTargetDate.setHours(23, 59, 59, 999);

        // Base filter: Created on selected date
        let filtered = allCustomers.filter(c => {
            const created = new Date(c.createdAt);
            const isInDate = created >= startOfTargetDate && created <= endOfTargetDate;
            return isInDate;
        });

        if (activeFilter === 'totalCustomers') {
            return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }

        console.log('📅 Date Filtered Count:', filtered.length);

        // Loan Type Filters - Check before status filters
        const loanFilters = ['carLoan', 'commercialLoan', 'homeLoan', 'lapLoan', 'personalLoan', 'businessLoan', 'workingCapitalLoan', 'agricultureLoan'];

        if (loanFilters.includes(activeFilter)) {
            // Filter from filtered array to show loans of this type for selected date range
            return filtered.filter(c => {
                const type = c.loanType;
                const subType = c.loanSubcategory; // Check subcategory as well

                // Vehicle Loan (Car + Commercial) - Match Keys OR Display Names in Type OR SubType OR Category
                if (activeFilter === 'carLoan') {
                    // Sub-filter Logic
                    if (subTypeFilter) {
                        if (subTypeFilter === 'newCar') return type === LOAN_TYPES.NEW_CAR || subType === LOAN_TYPES.NEW_CAR || type === LOAN_TYPE_NAMES[LOAN_TYPES.NEW_CAR];
                        if (subTypeFilter === 'usedCar') return type === LOAN_TYPES.USED_CAR || subType === LOAN_TYPES.USED_CAR || type === LOAN_TYPE_NAMES[LOAN_TYPES.USED_CAR];
                        if (subTypeFilter === 'newCommercial') return type === LOAN_TYPES.NEW_COMMERCIAL || subType === LOAN_TYPES.NEW_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.NEW_COMMERCIAL];
                        if (subTypeFilter === 'usedCommercial') return type === LOAN_TYPES.USED_COMMERCIAL || subType === LOAN_TYPES.USED_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.USED_COMMERCIAL];
                    }

                    return c.loanCategory === 'vehicle_loan' ||
                        type === LOAN_TYPES.NEW_CAR || type === LOAN_TYPE_NAMES[LOAN_TYPES.NEW_CAR] ||
                        subType === LOAN_TYPES.NEW_CAR ||
                        type === LOAN_TYPES.USED_CAR || type === LOAN_TYPE_NAMES[LOAN_TYPES.USED_CAR] ||
                        subType === LOAN_TYPES.USED_CAR ||
                        type === LOAN_TYPES.RTO_CAR || type === LOAN_TYPE_NAMES[LOAN_TYPES.RTO_CAR] ||
                        subType === LOAN_TYPES.RTO_CAR ||
                        type === LOAN_TYPES.NEW_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.NEW_COMMERCIAL] ||
                        subType === LOAN_TYPES.NEW_COMMERCIAL ||
                        type === LOAN_TYPES.USED_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.USED_COMMERCIAL] ||
                        subType === LOAN_TYPES.USED_COMMERCIAL ||
                        type === LOAN_TYPES.RTO_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.RTO_COMMERCIAL] ||
                        subType === LOAN_TYPES.RTO_COMMERCIAL;
                }

                // Other Loan Types (Keep original separate logic if needed, but Vehicle covers Commercial now)
                if (activeFilter === 'commercialLoan') {
                    return type === LOAN_TYPES.NEW_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.NEW_COMMERCIAL] ||
                        subType === LOAN_TYPES.NEW_COMMERCIAL ||
                        type === LOAN_TYPES.USED_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.USED_COMMERCIAL] ||
                        subType === LOAN_TYPES.USED_COMMERCIAL ||
                        type === LOAN_TYPES.RTO_COMMERCIAL || type === LOAN_TYPE_NAMES[LOAN_TYPES.RTO_COMMERCIAL] ||
                        subType === LOAN_TYPES.RTO_COMMERCIAL;
                }

                // Home Loan
                if (activeFilter === 'homeLoan') {
                    if (subTypeFilter) {
                        const smap = {
                            constructionBusiness: LOAN_TYPES.HOME_LOAN_CONSTRUCTION_BUSINESS,
                            constructionSalaried: LOAN_TYPES.HOME_LOAN_CONSTRUCTION_SALARIED,
                            flatPurchaseBusiness: LOAN_TYPES.FLAT_PURCHASE_BUSINESS,
                            flatPurchaseSalaried: LOAN_TYPES.FLAT_PURCHASE_SALARIED,
                            btBusiness: LOAN_TYPES.HOME_LOAN_BT_BUSINESS,
                            btSalaried: LOAN_TYPES.HOME_LOAN_BT_SALARY,
                            commercialPurchase: LOAN_TYPES.COMMERCIAL_PURCHASE
                        };
                        return type === smap[subTypeFilter]; // Using key match for exactness
                    }
                    return type === LOAN_TYPES.HOME_LOAN_CONSTRUCTION_BUSINESS || type === LOAN_TYPES.HOME_LOAN_CONSTRUCTION_SALARIED || type === LOAN_TYPES.FLAT_PURCHASE_SALARIED || type === LOAN_TYPES.FLAT_PURCHASE_BUSINESS || type === LOAN_TYPES.HOME_LOAN_BT_BUSINESS || type === LOAN_TYPES.HOME_LOAN_BT_SALARY || type === LOAN_TYPES.COMMERCIAL_PURCHASE || type === LOAN_TYPES.HOME_LOAN;
                }

                if (activeFilter === 'lapLoan') {
                    if (subTypeFilter) {
                        if (subTypeFilter === 'lapBusiness') return type === LOAN_TYPES.LAP_BUSINESS;
                        if (subTypeFilter === 'lapGavthan') return type === LOAN_TYPES.LAP_GAVTHAN;
                    }
                    return type === LOAN_TYPES.LAP_BUSINESS || type === LOAN_TYPES.LAP_GAVTHAN;
                }

                if (activeFilter === 'personalLoan') return type === LOAN_TYPES.PERSONAL_LOAN;

                if (activeFilter === 'businessLoan') {
                    if (subTypeFilter) {
                        if (subTypeFilter === 'business') return type === LOAN_TYPES.BUSINESS_LOAN;
                        if (subTypeFilter === 'project') return type === LOAN_TYPES.PROJECT_LOAN;
                        if (subTypeFilter === 'machinery') return type === LOAN_TYPES.MACHINERY_LOAN;
                    }
                    return type === LOAN_TYPES.BUSINESS_LOAN || type === LOAN_TYPES.PROJECT_LOAN || type === LOAN_TYPES.MACHINERY_LOAN;
                }

                if (activeFilter === 'workingCapitalLoan') return type === LOAN_TYPES.WORKING_CAPITAL;

                if (activeFilter === 'agricultureLoan') {
                    if (subTypeFilter) {
                        if (subTypeFilter === 'agriculture') return type === LOAN_TYPES.AGRICULTURE_LOAN;
                        if (subTypeFilter === 'animal') return type === LOAN_TYPES.ANIMAL_LOAN;
                    }
                    return type === LOAN_TYPES.AGRICULTURE_LOAN || type === LOAN_TYPES.ANIMAL_LOAN;
                }
                return false;
            });
        }

        const statusFilters = {
            'daily': () => true, // Already filtered by date
            'totalCustomers': () => true, // Handled above
            'login': (status) => {
                const s = status.toLowerCase();
                return s !== 'new lead' && !s.includes('sanction') && !s.includes('approval') && !s.includes('disburs') && !s.includes('payout') && !s.includes('reject');
            },
            'sanctioned': (status) => status.toLowerCase().includes('sanction') || status.toLowerCase().includes('approval'),
            'disbursed': (status, customer) => {
                // Filter by loanDisbursementDate falling in the selected range
                if (!customer.loanDisbursementDate) return false;
                const d = new Date(customer.loanDisbursementDate);
                const start = new Date(fromDate); start.setHours(0, 0, 0, 0);
                const end = new Date(toDate); end.setHours(23, 59, 59, 999);
                return d >= start && d <= end;
            },
            'rejected': (status) => status.toLowerCase().includes('reject'),
            'financeScheme': (c) => {
                // Filter for finance schemes created on selected date range
                if (!c.financeScheme) return false;
                const start = new Date(fromDate);
                start.setHours(0, 0, 0, 0);
                const end = new Date(toDate);
                end.setHours(23, 59, 59, 999);

                const schemeCreated = c.financeScheme.createdAt ? new Date(c.financeScheme.createdAt) : new Date(c.createdAt);
                return schemeCreated >= start && schemeCreated <= end;
            }
        };

        if (activeFilter !== 'daily' && activeFilter !== 'totalCustomers') {
            if (activeFilter === 'financeScheme') {
                // Custom filter logic for finance scheme which uses its own date or matches main filter
                filtered = allCustomers.filter(statusFilters['financeScheme']);
            } else if (activeFilter === 'disbursed') {
                // disbursed uses loanDisbursementDate, search across ALL customers (not just date-filtered)
                filtered = allCustomers.filter(c => statusFilters['disbursed'](c.status || '', c));
            } else {
                filtered = filtered.filter(c => statusFilters[activeFilter]?.(c.status || ''));
            }
        }

        return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    };

    const filteredList = getFilteredCustomers();

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"></div>
                <p className="text-gray-600 text-lg">Loading admin dashboard...</p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-none">
            {/* Header */}
            <div className="mb-6 sm:mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="space-y-2 w-full md:w-auto">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg transform hover:rotate-12 transition-transform duration-300">
                                <FaChartLine className="text-white text-lg sm:text-xl" />
                            </div>
                            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent tracking-tight">
                                Admin Dashboard
                            </h1>
                        </div>
                        <p className="text-gray-600 ml-14 flex items-center gap-2 text-sm sm:text-base">
                            <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            Overview of daily activities and business metrics
                        </p>
                    </div>

                    {/* Date Filter & Refresh */}
                    <div className="flex flex-col xl:flex-row items-stretch xl:items-center gap-3 w-full xl:w-auto">

                        {/* Mobile: Grid for Date Inputs, Desktop: Flex */}
                        <div className="bg-white p-2 rounded-xl shadow-sm border border-gray-200">
                            <div className="grid grid-cols-2 sm:flex sm:items-center gap-2">
                                <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">From</span>
                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        className="w-full sm:w-auto pl-10 pr-2 py-2 bg-gray-50 sm:bg-transparent border border-gray-100 sm:border-none rounded-lg text-gray-700 text-sm font-semibold focus:ring-0 cursor-pointer"
                                    />
                                </div>
                                <div className="hidden sm:block h-6 w-px bg-gray-200"></div>
                                <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">To</span>
                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        className="w-full sm:w-auto pl-8 pr-2 py-2 bg-gray-50 sm:bg-transparent border border-gray-100 sm:border-none rounded-lg text-gray-700 text-sm font-semibold focus:ring-0 cursor-pointer"
                                    />
                                </div>
                                <div className="col-span-2 sm:col-span-1 flex gap-2">
                                    <button
                                        onClick={() => {
                                            const today = getTodayString();
                                            setFromDate(today);
                                            setToDate(today);
                                        }}
                                        className="w-full sm:w-auto px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-bold hover:bg-blue-100 transition-colors"
                                    >
                                        Today
                                    </button>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => loadDashboardData(true)}
                            className="w-full xl:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2.5 rounded-xl shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 font-bold"
                        >
                            <span className={`text-xl ${loading ? 'animate-spin' : ''}`}>🔄</span>
                            <span>Refresh Data</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Target Progress Card (Moved to Top) + Stat Cards to the right */}
            <div className="mb-6 flex flex-col lg:flex-row items-start gap-6">
                <div className="relative overflow-hidden rounded-2xl p-4 bg-white border-2 border-emerald-100 hover:border-emerald-400 hover:shadow-lg transition-all duration-300 group w-full lg:w-1/3 max-w-sm">
                    <div className="relative z-10 flex flex-col h-full">
                        <div className="flex justify-between items-start mb-2">
                            <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100 transition-colors">
                                <FaBullseye className="text-xl" />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setTargetMode('yearly');
                                        setNewTarget(dashboardData.yearlyTarget);
                                        setShowTargetModal(true);
                                    }}
                                    className="text-[10px] bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-1 rounded-lg transition-colors flex items-center gap-1 border border-blue-100"
                                >
                                    <FaEdit className="mr-1" /> Set Yearly Target
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col items-center">
                            {/* View Toggle */}
                            <div className="flex bg-gray-100 p-1 rounded-lg mb-2">
                                <button
                                    onClick={() => setViewMode('month')}
                                    className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${viewMode === 'month' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    This Month
                                </button>
                                <button
                                    onClick={() => setViewMode('year')}
                                    className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${viewMode === 'year' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    This Year
                                </button>
                            </div>

                            {/* Gauge Chart */}
                            <div className="relative w-48 h-24 mb-2">
                                <ResponsiveContainer width="100%" height="200%">
                                    <PieChart>
                                        <Pie
                                            data={
                                                viewMode === 'month' ?
                                                    [
                                                        { name: 'Achieved', value: Math.min(dashboardData.monthlyTarget || 1, dashboardData.achievedAmount || 0) },
                                                        { name: 'Remaining', value: Math.max(0, (dashboardData.monthlyTarget || 1) - (dashboardData.achievedAmount || 0)) }
                                                    ] :
                                                    [
                                                        { name: 'Achieved', value: Math.min(dashboardData.yearlyTarget || 1, dashboardData.yearToDateDisbursed || 0) },
                                                        { name: 'Remaining', value: Math.max(0, (dashboardData.yearlyTarget || 1) - (dashboardData.yearToDateDisbursed || 0)) }
                                                    ]
                                            }
                                            cx="50%"
                                            cy="50%"
                                            startAngle={180}
                                            endAngle={0}
                                            innerRadius={60}
                                            outerRadius={80}
                                            paddingAngle={0}
                                            dataKey="value"
                                            stroke="none"
                                        >
                                            <Cell key="achieved" fill={viewMode === 'month' ? '#10B981' : '#3B82F6'} />
                                            <Cell key="remaining" fill="#E5E7EB" />
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="absolute inset-0 flex flex-col items-center justify-end pb-2 pointer-events-none">
                                    <span className={`text-2xl font-bold ${viewMode === 'month' ? 'text-emerald-600' : 'text-blue-600'}`}>
                                        {viewMode === 'month'
                                            ? (dashboardData.monthlyTarget > 0 ? Math.min(100, Math.round(((dashboardData.achievedAmount || 0) / dashboardData.monthlyTarget) * 100)) : 0)
                                            : (dashboardData.yearlyTarget > 0 ? Math.min(100, Math.round(((dashboardData.yearToDateDisbursed || 0) / dashboardData.yearlyTarget) * 100)) : 0)
                                        }%
                                    </span>
                                    <span className="text-[10px] text-gray-400 uppercase tracking-wider">
                                        {viewMode === 'month' ? 'Month Goal' : 'Year Goal'}
                                    </span>
                                </div>
                            </div>

                            <div className="w-full grid grid-cols-2 gap-2 text-center mt-2">
                                <div className={`${viewMode === 'month' ? 'bg-emerald-50' : 'bg-blue-50'} rounded-lg p-2`}>
                                    <div className={`text-[10px] font-medium mb-0.5 ${viewMode === 'month' ? 'text-emerald-600' : 'text-blue-600'}`}>
                                        {viewMode === 'month' ? 'Disbursed (M)' : 'Disbursed (YTD)'}
                                    </div>
                                    <div className={`text-sm font-bold ${viewMode === 'month' ? 'text-emerald-700' : 'text-blue-700'}`}>
                                        {formatCurrency(viewMode === 'month' ? (dashboardData.achievedAmount || 0) : (dashboardData.yearToDateDisbursed || 0))}
                                    </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-2">
                                    <div className="text-[10px] text-gray-500 font-medium mb-0.5">Target</div>
                                    <div className="text-sm font-bold text-gray-700">
                                        {formatCurrency(viewMode === 'month' ? (dashboardData.monthlyTarget || 0) : (dashboardData.yearlyTarget || 0))}
                                    </div>
                                </div>
                            </div>

                            {/* Monthly Breakdown Info - Compact (Only show for Month View) */}
                            {viewMode === 'month' && (
                                <div className="mt-3 flex justify-between w-full text-[10px] text-gray-400 border-t border-gray-100 pt-2">
                                    <span>Base: {formatCurrency(dashboardData.monthlyBase || 0)}</span>
                                    <span className={
                                        dashboardData.carryover > 0
                                            ? 'text-red-400'
                                            : dashboardData.carryover < 0
                                                ? 'text-green-500'
                                                : 'text-gray-400'
                                    }>
                                        {dashboardData.carryover > 0
                                            ? `+${formatCurrency(dashboardData.carryover)} shortfall`
                                            : dashboardData.carryover < 0
                                                ? `-${formatCurrency(Math.abs(dashboardData.carryover))} surplus`
                                                : 'On track'}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Small stat cards placed to the right of target to fill horizontal space */}
                <div className="w-full lg:w-2/3">
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                            {/* 1. Total Customers */}
                            <div onClick={() => setActiveFilter(activeFilter === 'totalCustomers' ? null : 'totalCustomers')}
                                className={`relative overflow-hidden rounded-2xl p-4 cursor-pointer transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl group
                                    ${activeFilter === 'totalCustomers' ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-200 ring-2 ring-blue-400 ring-offset-2 scale-105' : 'bg-white border-2 border-blue-100 hover:border-blue-400 hover:shadow-lg'}`}>
                                <div className="relative z-10 flex flex-col h-full justify-between">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className={`p-2 rounded-xl transition-colors duration-300 ${activeFilter === 'totalCustomers' ? 'bg-white/20 backdrop-blur-sm' : 'bg-blue-50 text-blue-600 group-hover:bg-blue-100'}`}>
                                            <FaEdit className="text-xl" />
                                        </div>
                                        {activeFilter === 'totalCustomers' && <div className="animate-pulse w-2 h-2 rounded-full bg-white"></div>}
                                    </div>
                                    <div>
                                        <div className={`text-3xl font-bold mb-1 ${activeFilter === 'totalCustomers' ? 'text-white' : 'text-gray-800'}`}>{dashboardData.totalCustomers}</div>
                                        <div className={`text-sm font-medium ${activeFilter === 'totalCustomers' ? 'text-blue-100' : 'text-gray-500'}`}>Total Customer List</div>
                                    </div>
                                </div>
                                <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 transition-transform duration-500 group-hover:scale-150 ${activeFilter === 'totalCustomers' ? 'bg-white' : 'bg-blue-500'}`}></div>
                            </div>

                            {/* 2. Login (Processing) */}
                            <div onClick={() => setActiveFilter(activeFilter === 'login' ? null : 'login')}
                                className={`relative overflow-hidden rounded-2xl p-4 cursor-pointer transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl group
                                    ${activeFilter === 'login' ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white shadow-lg shadow-yellow-200 ring-2 ring-yellow-400 ring-offset-2 scale-105' : 'bg-white border-2 border-yellow-100 hover:border-yellow-400 hover:shadow-lg'}`}>
                                <div className="relative z-10 flex flex-col h-full justify-between">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className={`p-2 rounded-xl transition-colors duration-300 ${activeFilter === 'login' ? 'bg-white/20 backdrop-blur-sm' : 'bg-yellow-50 text-yellow-600 group-hover:bg-yellow-100'}`}>
                                            <FaFolderOpen className="text-xl" />
                                        </div>
                                        {activeFilter === 'login' && <div className="animate-pulse w-2 h-2 rounded-full bg-white"></div>}
                                    </div>
                                    <div>
                                        <div className={`text-3xl font-bold mb-1 ${activeFilter === 'login' ? 'text-white' : 'text-gray-800'}`}>{dashboardData.loginCount}</div>
                                        <div className={`text-sm font-medium ${activeFilter === 'login' ? 'text-yellow-50' : 'text-gray-500'}`}>Login / Process</div>
                                    </div>
                                </div>
                                <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 transition-transform duration-500 group-hover:scale-150 ${activeFilter === 'login' ? 'bg-white' : 'bg-yellow-500'}`}></div>
                            </div>

                            {/* 3. Sanctioned */}
                            <div onClick={() => setActiveFilter(activeFilter === 'sanctioned' ? null : 'sanctioned')}
                                className={`relative overflow-hidden rounded-2xl p-4 cursor-pointer transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl group
                                    ${activeFilter === 'sanctioned' ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg shadow-green-200 ring-2 ring-green-400 ring-offset-2 scale-105' : 'bg-white border-2 border-green-100 hover:border-green-400 hover:shadow-lg'}`}>
                                <div className="relative z-10 flex flex-col h-full justify-between">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className={`p-2 rounded-xl transition-colors duration-300 ${activeFilter === 'sanctioned' ? 'bg-white/20 backdrop-blur-sm' : 'bg-green-50 text-green-600 group-hover:bg-green-100'}`}>
                                            <FaCheckCircle className="text-xl" />
                                        </div>
                                        {activeFilter === 'sanctioned' && <div className="animate-pulse w-2 h-2 rounded-full bg-white"></div>}
                                    </div>
                                    <div>
                                        <div className={`text-3xl font-bold mb-1 ${activeFilter === 'sanctioned' ? 'text-white' : 'text-gray-800'}`}>{dashboardData.sanctionedCount}</div>
                                        <div className={`text-sm font-medium ${activeFilter === 'sanctioned' ? 'text-green-100' : 'text-gray-500'}`}>Sanctioned</div>
                                    </div>
                                </div>
                                <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 transition-transform duration-500 group-hover:scale-150 ${activeFilter === 'sanctioned' ? 'bg-white' : 'bg-green-500'}`}></div>
                            </div>
                        </div>

                        <div className="flex gap-3 sm:gap-4">
                            <div className="flex-1">
                                {/* 4. Disbursed */}
                                <div onClick={() => setActiveFilter(activeFilter === 'disbursed' ? null : 'disbursed')}
                                    className={`relative overflow-hidden rounded-2xl p-4 cursor-pointer transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl group
                                        ${activeFilter === 'disbursed' ? 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white shadow-lg shadow-purple-200 ring-2 ring-purple-400 ring-offset-2 scale-105' : 'bg-white border-2 border-purple-100 hover:border-purple-400 hover:shadow-lg'}`}>
                                    <div className="relative z-10 flex flex-col h-full justify-between">
                                        <div className="flex justify-between items-start mb-2">
                                            <div className={`p-2 rounded-xl transition-colors duration-300 ${activeFilter === 'disbursed' ? 'bg-white/20 backdrop-blur-sm' : 'bg-purple-50 text-purple-600 group-hover:bg-purple-100'}`}>
                                                <FaMoneyBillWave className="text-xl" />
                                            </div>
                                            {activeFilter === 'disbursed' && <div className="animate-pulse w-2 h-2 rounded-full bg-white"></div>}
                                        </div>
                                        <div>
                                            <div className={`text-3xl font-bold mb-1 ${activeFilter === 'disbursed' ? 'text-white' : 'text-gray-800'}`}>{dashboardData.disbursedCount}</div>
                                            <div className={`text-sm font-medium ${activeFilter === 'disbursed' ? 'text-purple-100' : 'text-gray-500'}`}>Disbursed</div>
                                        </div>
                                    </div>
                                    <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 transition-transform duration-500 group-hover:scale-150 ${activeFilter === 'disbursed' ? 'bg-white' : 'bg-purple-500'}`}></div>
                                </div>
                            </div>

                            <div className="flex-1">
                                {/* 5. Rejected */}
                                <div onClick={() => setActiveFilter(activeFilter === 'rejected' ? null : 'rejected')}
                                    className={`relative overflow-hidden rounded-2xl p-4 cursor-pointer transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl group
                                        ${activeFilter === 'rejected' ? 'bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-lg shadow-red-200 ring-2 ring-red-400 ring-offset-2 scale-105' : 'bg-white border-2 border-red-100 hover:border-red-400 hover:shadow-lg'}`}>
                                    <div className="relative z-10 flex flex-col h-full justify-between">
                                        <div className="flex justify-between items-start mb-2">
                                            <div className={`p-2 rounded-xl transition-colors duration-300 ${activeFilter === 'rejected' ? 'bg-white/20 backdrop-blur-sm' : 'bg-red-50 text-red-600 group-hover:bg-red-100'}`}>
                                                <FaTimesCircle className="text-xl" />
                                            </div>
                                            {activeFilter === 'rejected' && <div className="animate-pulse w-2 h-2 rounded-full bg-white"></div>}
                                        </div>
                                        <div>
                                            <div className={`text-3xl font-bold mb-1 ${activeFilter === 'rejected' ? 'text-white' : 'text-gray-800'}`}>{dashboardData.rejectedCount}</div>
                                            <div className={`text-sm font-medium ${activeFilter === 'rejected' ? 'text-red-100' : 'text-gray-500'}`}>Rejected</div>
                                        </div>
                                    </div>
                                    <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 transition-transform duration-500 group-hover:scale-150 ${activeFilter === 'rejected' ? 'bg-white' : 'bg-red-500'}`}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Three Sections Below Cards */}
            {/*
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6">
                    <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center">
                        <span className="text-lg sm:text-xl lg:text-2xl mr-2">🎯</span>
                        <span className="text-sm sm:text-base lg:text-lg">Most Cases Referred By</span>
                    </h3>
                    <div className="space-y-2 sm:space-y-3">
                        {dashboardData.mostCasesReferredBy.length > 0 ? (
                            dashboardData.mostCasesReferredBy.map((referrer, index) => (
                                <div key={index} className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center min-w-0 flex-1">
                                        <div className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold mr-2 sm:mr-3 flex-shrink-0">
                                            {index + 1}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <span className="font-medium text-gray-800 block text-xs sm:text-sm lg:text-base truncate">{referrer.name}</span>
                                            <span className="text-xs text-gray-500">{referrer.count} referrals</span>
                                        </div>
                                    </div>
                                    <span className="bg-blue-500 text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-semibold ml-2 flex-shrink-0">
                                        {referrer.percentage}%
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-4 sm:py-6 lg:py-8 text-gray-500">
                                <div className="text-2xl sm:text-3xl lg:text-4xl mb-2">📝</div>
                                <p className="text-xs sm:text-sm lg:text-base">No referral data available</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6">
                    <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center">
                        <span className="text-lg sm:text-xl lg:text-2xl mr-2">🕒</span>
                        <span className="text-sm sm:text-base lg:text-lg">Recent Loans (Top 5)</span>
                    </h3>
                    <div className="space-y-2 sm:space-y-3">
                        {dashboardData.recentLoans.length > 0 ? (
                            dashboardData.recentLoans.map((loan) => (
                                <div key={loan.srNo} className="border-l-4 border-green-500 pl-2 sm:pl-3 lg:pl-4 py-2">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1">
                                        <span className="font-semibold text-gray-800 text-xs sm:text-sm lg:text-base">
                                            {loan.srNo}. {loan.customerName}
                                        </span>
                                        <span className="text-xs text-gray-500 mt-1 sm:mt-0">
                                            {formatDate(loan.createdAt)}
                                        </span>
                                    </div>
                                    <div className="text-xs sm:text-sm text-gray-600">
                                        {loan.loanType}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-4 sm:py-6 lg:py-8 text-gray-500">
                                <div className="text-2xl sm:text-3xl lg:text-4xl mb-2">📋</div>
                                <p className="text-xs sm:text-sm lg:text-base">No recent loans available</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6">
                    <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center">
                        <span className="text-lg sm:text-xl lg:text-2xl mr-2">💰</span>
                        <span className="text-sm sm:text-base lg:text-lg">Monthly Commission</span>
                    </h3>
                    <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 lg:p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center min-w-0 flex-1">
                                    <span className="text-base sm:text-lg lg:text-2xl mr-2 flex-shrink-0">📈</span>
                                    <span className="font-semibold text-green-800 text-xs sm:text-sm lg:text-base">Total Get</span>
                                </div>
                                <span className="text-sm sm:text-lg lg:text-xl font-bold text-green-600 ml-2">
                                    {formatCurrency(dashboardData.monthlyCommission.totalGet)}
                                </span>
                            </div>
                        </div>

                        <div className="bg-red-50 border border-red-200 rounded-lg p-2 sm:p-3 lg:p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center min-w-0 flex-1">
                                    <span className="text-base sm:text-lg lg:text-2xl mr-2 flex-shrink-0">📉</span>
                                    <span className="font-semibold text-red-800 text-xs sm:text-sm lg:text-base">Total Given</span>
                                </div>
                                <span className="text-sm sm:text-lg lg:text-xl font-bold text-red-600 ml-2">
                                    {formatCurrency(dashboardData.monthlyCommission.totalGiven)}
                                </span>
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 lg:p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center min-w-0 flex-1">
                                    <span className="text-base sm:text-lg lg:text-2xl mr-2 flex-shrink-0">💎</span>
                                    <span className="font-semibold text-blue-800 text-xs sm:text-sm lg:text-base">Net Profit</span>
                                </div>
                                <span className="text-sm sm:text-lg lg:text-xl font-bold text-blue-600 ml-2">
                                    {formatCurrency(dashboardData.monthlyCommission.totalGet - dashboardData.monthlyCommission.totalGiven)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* stat cards moved above next to target */}

            {/* Target Setting Modal */}
            {
                showTargetModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 animate-scale-up">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Set {targetMode === 'monthly' ? 'Monthly' : 'Yearly'} Target</h3>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">{targetMode === 'monthly' ? 'Monthly' : 'Yearly'} Target Amount (₹)</label>
                                <input
                                    type="number"
                                    value={newTarget}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '' || parseFloat(val) >= 0) {
                                            setNewTarget(val);
                                        }
                                    }}
                                    min="0"
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                                    placeholder={`Enter ${targetMode} target amount`}
                                />
                            </div>
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => setShowTargetModal(false)}
                                    className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateTarget}
                                    className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-medium shadow-md hover:shadow-lg transition-all"
                                >
                                    Save Target
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Loan Type Cards Section */}
            <h2 className="text-xl font-bold text-gray-800 mb-4 px-1">Loan Types Overview</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {/* 1. Car Loan */}
                <div
                    onClick={() => setActiveFilter(activeFilter === 'carLoan' ? null : 'carLoan')}
                    className={`rounded-2xl p-4 border-l-4 border-blue-500 shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
                        ${activeFilter === 'carLoan' ? 'bg-blue-50 ring-2 ring-blue-500 transform scale-105' : 'bg-white'}`}
                >
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-gray-500 text-sm font-medium mb-1">Vehicle Loan</p>
                            <h3 className="text-2xl font-bold text-gray-800">{dashboardData.loanCounts.carLoan}</h3>
                        </div>
                        <div className={`p-3 rounded-full ${activeFilter === 'carLoan' ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-600'}`}>
                            <FaCar className="text-xl" />
                        </div>
                    </div>
                </div>

                {/* 3. Home Loan */}
                <div
                    onClick={() => setActiveFilter(activeFilter === 'homeLoan' ? null : 'homeLoan')}
                    className={`rounded-2xl p-4 border-l-4 border-green-500 shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
                        ${activeFilter === 'homeLoan' ? 'bg-green-50 ring-2 ring-green-500 transform scale-105' : 'bg-white'}`}
                >
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-gray-500 text-sm font-medium mb-1">Home Loan</p>
                            <h3 className="text-2xl font-bold text-gray-800">{dashboardData.loanCounts.homeLoan}</h3>
                        </div>
                        <div className={`p-3 rounded-full ${activeFilter === 'homeLoan' ? 'bg-green-500 text-white' : 'bg-green-50 text-green-600'}`}>
                            <FaHome className="text-xl" />
                        </div>
                    </div>
                </div>

                {/* 4. LAP / Mortgage */}
                <div
                    onClick={() => setActiveFilter(activeFilter === 'lapLoan' ? null : 'lapLoan')}
                    className={`rounded-2xl p-4 border-l-4 border-purple-500 shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
                        ${activeFilter === 'lapLoan' ? 'bg-purple-50 ring-2 ring-purple-500 transform scale-105' : 'bg-white'}`}
                >
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-gray-500 text-sm font-medium mb-1">LAP / Mortgage</p>
                            <h3 className="text-2xl font-bold text-gray-800">{dashboardData.loanCounts.lapLoan}</h3>
                        </div>
                        <div className={`p-3 rounded-full ${activeFilter === 'lapLoan' ? 'bg-purple-500 text-white' : 'bg-purple-50 text-purple-600'}`}>
                            <FaFileContract className="text-xl" />
                        </div>
                    </div>
                </div>

                {/* 5. Personal Loan */}
                <div
                    onClick={() => setActiveFilter(activeFilter === 'personalLoan' ? null : 'personalLoan')}
                    className={`rounded-2xl p-4 border-l-4 border-pink-500 shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
                        ${activeFilter === 'personalLoan' ? 'bg-pink-50 ring-2 ring-pink-500 transform scale-105' : 'bg-white'}`}
                >
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-gray-500 text-sm font-medium mb-1">Personal Loan</p>
                            <h3 className="text-2xl font-bold text-gray-800">{dashboardData.loanCounts.personalLoan}</h3>
                        </div>
                        <div className={`p-3 rounded-full ${activeFilter === 'personalLoan' ? 'bg-pink-500 text-white' : 'bg-pink-50 text-pink-600'}`}>
                            <FaUser className="text-xl" />
                        </div>
                    </div>
                </div>

                {/* 6. Business Loan */}
                <div
                    onClick={() => setActiveFilter(activeFilter === 'businessLoan' ? null : 'businessLoan')}
                    className={`rounded-2xl p-4 border-l-4 border-cyan-500 shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
                        ${activeFilter === 'businessLoan' ? 'bg-cyan-50 ring-2 ring-cyan-500 transform scale-105' : 'bg-white'}`}
                >
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-gray-500 text-sm font-medium mb-1">Business Loan</p>
                            <h3 className="text-2xl font-bold text-gray-800">{dashboardData.loanCounts.businessLoan}</h3>
                        </div>
                        <div className={`p-3 rounded-full ${activeFilter === 'businessLoan' ? 'bg-cyan-500 text-white' : 'bg-cyan-50 text-cyan-600'}`}>
                            <FaBriefcase className="text-xl" />
                        </div>
                    </div>
                </div>

                {/* 7. Working Capital Loan */}
                <div
                    onClick={() => setActiveFilter(activeFilter === 'workingCapitalLoan' ? null : 'workingCapitalLoan')}
                    className={`rounded-2xl p-4 border-l-4 border-yellow-500 shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
                        ${activeFilter === 'workingCapitalLoan' ? 'bg-yellow-50 ring-2 ring-yellow-500 transform scale-105' : 'bg-white'}`}
                >
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-gray-500 text-sm font-medium mb-1">Working Capital</p>
                            <h3 className="text-2xl font-bold text-gray-800">{dashboardData.loanCounts.workingCapitalLoan}</h3>
                        </div>
                        <div className={`p-3 rounded-full ${activeFilter === 'workingCapitalLoan' ? 'bg-yellow-500 text-white' : 'bg-yellow-50 text-yellow-600'}`}>
                            <FaMoneyBillWave className="text-xl" />
                        </div>
                    </div>
                </div>

                {/* 8. Agriculture Loan */}
                <div
                    onClick={() => setActiveFilter(activeFilter === 'agricultureLoan' ? null : 'agricultureLoan')}
                    className={`rounded-2xl p-4 border-l-4 border-emerald-500 shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
                        ${activeFilter === 'agricultureLoan' ? 'bg-emerald-50 ring-2 ring-emerald-500 transform scale-105' : 'bg-white'}`}
                >
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-gray-500 text-sm font-medium mb-1">Agriculture Loan</p>
                            <h3 className="text-2xl font-bold text-gray-800">{dashboardData.loanCounts.agricultureLoan}</h3>
                        </div>
                        <div className={`p-3 rounded-full ${activeFilter === 'agricultureLoan' ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-600'}`}>
                            <FaTractor className="text-xl" />
                        </div>
                    </div>
                </div>

                {/* 9. Disbursed Total Amount */}
                <div
                    onClick={() => setActiveFilter(activeFilter === 'disbursed' ? null : 'disbursed')}
                    className={`rounded-2xl p-4 border-l-4 border-green-600 shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
                        ${activeFilter === 'disbursed' ? 'bg-green-50 ring-2 ring-green-600 transform scale-105' : 'bg-white'}`}
                >
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-gray-500 text-sm font-medium mb-1">Disbursed Total Amount</p>
                            <h3 className="text-xl font-bold text-green-700">
                                {dashboardData.totalDisbursedAmount > 0
                                    ? formatCurrency(dashboardData.totalDisbursedAmount)
                                    : '₹0'}
                            </h3>
                            <p className="text-xs text-gray-400 mt-1">
                                {dashboardData.disbursedCount || 0} loan{(dashboardData.disbursedCount || 0) !== 1 ? 's' : ''} disbursed
                            </p>
                        </div>
                        <div className={`p-3 rounded-full ${activeFilter === 'disbursed' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-600'}`}>
                            <FaMoneyBillWave className="text-xl" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Vehicle Loan Subtypes Tabs */}
            {
                activeFilter === 'carLoan' && (
                    <div className="mb-6 pl-1 animate-fade-in-down">
                        <p className="text-sm font-medium text-gray-500 mb-3 ml-1">Select specific vehicle type:</p>
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={() => setSubTypeFilter(null)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${!subTypeFilter ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                All ({dashboardData.loanCounts?.carLoan || 0})
                            </button>
                            <button
                                onClick={() => setSubTypeFilter('newCar')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'newCar' ? 'bg-blue-500 text-white shadow-md' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                            >
                                🚗 New Car ({dashboardData.vehicleSubCounts?.newCar || 0})
                            </button>
                            <button
                                onClick={() => setSubTypeFilter('usedCar')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'usedCar' ? 'bg-indigo-500 text-white shadow-md' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}
                            >
                                🚗 Used Car ({dashboardData.vehicleSubCounts?.usedCar || 0})
                            </button>
                            <button
                                onClick={() => setSubTypeFilter('newCommercial')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'newCommercial' ? 'bg-orange-500 text-white shadow-md' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'}`}
                            >
                                🚚 New Commercial ({dashboardData.vehicleSubCounts?.newCommercial || 0})
                            </button>
                            <button
                                onClick={() => setSubTypeFilter('usedCommercial')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'usedCommercial' ? 'bg-red-500 text-white shadow-md' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                            >
                                🚚 Used Commercial ({dashboardData.vehicleSubCounts?.usedCommercial || 0})
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Home Loan Subtypes Tabs */}
            {
                activeFilter === 'homeLoan' && (
                    <div className="mb-6 pl-1 animate-fade-in-down">
                        <p className="text-sm font-medium text-gray-500 mb-3 ml-1">Select specific Home Loan type:</p>
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={() => setSubTypeFilter(null)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${!subTypeFilter ? 'bg-emerald-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                All ({dashboardData.loanCounts?.homeLoan || 0})
                            </button>
                            <button
                                onClick={() => setSubTypeFilter('constructionBusiness')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'constructionBusiness' ? 'bg-blue-500 text-white shadow-md' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                            >
                                🏗️ Construction (Biz) ({dashboardData.homeSubCounts?.constructionBusiness || 0})
                            </button>
                            <button
                                onClick={() => setSubTypeFilter('constructionSalaried')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'constructionSalaried' ? 'bg-cyan-500 text-white shadow-md' : 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100'}`}
                            >
                                🏗️ Construction (Sal) ({dashboardData.homeSubCounts?.constructionSalaried || 0})
                            </button>
                            <button
                                onClick={() => setSubTypeFilter('flatPurchaseBusiness')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'flatPurchaseBusiness' ? 'bg-purple-500 text-white shadow-md' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}
                            >
                                🏢 Flat Purchase (Biz) ({dashboardData.homeSubCounts?.flatPurchaseBusiness || 0})
                            </button>
                            <button
                                onClick={() => setSubTypeFilter('flatPurchaseSalaried')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'flatPurchaseSalaried' ? 'bg-pink-500 text-white shadow-md' : 'bg-pink-50 text-pink-700 hover:bg-pink-100'}`}
                            >
                                🏢 Flat Purchase (Sal) ({dashboardData.homeSubCounts?.flatPurchaseSalaried || 0})
                            </button>
                            <button
                                onClick={() => setSubTypeFilter('btBusiness')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'btBusiness' ? 'bg-orange-500 text-white shadow-md' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'}`}
                            >
                                🔄 BT (Business) ({dashboardData.homeSubCounts?.btBusiness || 0})
                            </button>
                            <button
                                onClick={() => setSubTypeFilter('btSalaried')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'btSalaried' ? 'bg-amber-500 text-white shadow-md' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                            >
                                🔄 BT (Salary) ({dashboardData.homeSubCounts?.btSalaried || 0})
                            </button>
                            <button
                                onClick={() => setSubTypeFilter('commercialPurchase')}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'commercialPurchase' ? 'bg-emerald-500 text-white shadow-md' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                            >
                                🏪 Commercial ({dashboardData.homeSubCounts?.commercialPurchase || 0})
                            </button>
                        </div>
                    </div>
                )
            }

            {/* LAP Subtypes Tabs */}
            {
                activeFilter === 'lapLoan' && (
                    <div className="mb-6 pl-1 animate-fade-in-down">
                        <p className="text-sm font-medium text-gray-500 mb-3 ml-1">Select specific LAP type:</p>
                        <div className="flex flex-wrap gap-3">
                            <button onClick={() => setSubTypeFilter(null)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${!subTypeFilter ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All ({dashboardData.loanCounts?.lapLoan || 0})</button>
                            <button onClick={() => setSubTypeFilter('lapBusiness')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'lapBusiness' ? 'bg-purple-500 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}>LAP (Business) ({dashboardData.lapSubCounts?.lapBusiness || 0})</button>
                            <button onClick={() => setSubTypeFilter('lapGavthan')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'lapGavthan' ? 'bg-purple-500 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}>LAP (Gavthan Property) ({dashboardData.lapSubCounts?.lapGavthan || 0})</button>
                        </div>
                    </div>
                )
            }

            {/* Business Loan Subtypes Tabs */}
            {
                activeFilter === 'businessLoan' && (
                    <div className="mb-6 pl-1 animate-fade-in-down">
                        <p className="text-sm font-medium text-gray-500 mb-3 ml-1">Select specific Business Loan type:</p>
                        <div className="flex flex-wrap gap-3">
                            <button onClick={() => setSubTypeFilter(null)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${!subTypeFilter ? 'bg-cyan-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All ({dashboardData.loanCounts?.businessLoan || 0})</button>
                            <button onClick={() => setSubTypeFilter('business')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'business' ? 'bg-cyan-500 text-white' : 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100'}`}>Business Loan ({dashboardData.businessSubCounts?.business || 0})</button>
                            <button onClick={() => setSubTypeFilter('project')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'project' ? 'bg-cyan-500 text-white' : 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100'}`}>Project Loan ({dashboardData.businessSubCounts?.project || 0})</button>
                            <button onClick={() => setSubTypeFilter('machinery')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${subTypeFilter === 'machinery' ? 'bg-cyan-500 text-white' : 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100'}`}>Machinery Loan ({dashboardData.businessSubCounts?.machinery || 0})</button>
                        </div>
                    </div>
                )
            }

            {/* Filtered List Section */}
            {
                activeFilter && (
                    <div className="bg-white rounded-lg sm:rounded-xl shadow-lg mb-4 sm:mb-6 lg:mb-8 overflow-hidden animate-fade-in-down">
                        <div className="p-3 sm:p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h3 className="text-base sm:text-lg font-bold text-gray-800">
                                {activeFilter === 'totalCustomers' && '📝 Total Customer List'}
                                {activeFilter === 'login' && '📂 Processing Files (Login)'}
                                {activeFilter === 'sanctioned' && '✅ Sanctioned Loans'}
                                {activeFilter === 'disbursed' && '💰 Disbursed Loans'}
                                {activeFilter === 'rejected' && '❌ Rejected Loans'}
                                {activeFilter === 'financeScheme' && '🏦 Finance Schemes'}
                                {activeFilter === 'carLoan' && '🚗 Vehicle Loans'}
                                {activeFilter === 'homeLoan' && '🏠 Home Loans'}
                                {activeFilter === 'lapLoan' && '📜 LAP / Mortgage'}
                                {activeFilter === 'personalLoan' && '👤 Personal Loans'}
                                {activeFilter === 'businessLoan' && '💼 Business Loans'}
                                {activeFilter === 'workingCapitalLoan' && '💵 Working Capital'}
                                {activeFilter === 'agricultureLoan' && '🚜 Agriculture Loans'}
                                <span className="ml-2 text-xs sm:text-sm font-normal text-gray-500">({filteredList.length} records)</span>
                            </h3>
                            <button onClick={() => setActiveFilter(null)} className="text-gray-400 hover:text-red-500 p-1">✖</button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse min-w-full">
                                <thead>
                                    <tr className="bg-gray-100 text-gray-600 uppercase text-xs">
                                        <th className="p-2 sm:p-3">Sr.No</th>
                                        <th className="p-2 sm:p-3">Customer Name</th>
                                        {activeFilter === 'financeScheme' ? (
                                            <>
                                                <th className="p-2 sm:p-3">Finance Company</th>
                                                <th className="p-2 sm:p-3">Scheme Amount</th>
                                                <th className="p-2 sm:p-3">Start Date</th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="p-2 sm:p-3 hidden sm:table-cell">Loan Type</th>
                                                <th className="p-2 sm:p-3">Status</th>
                                                <th className="p-2 sm:p-3">Remark</th>
                                                {activeFilter === 'disbursed' && (
                                                    <th className="p-2 sm:p-3 hidden sm:table-cell">Amount</th>
                                                )}
                                                <th className="p-2 sm:p-3 hidden md:table-cell">Date</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredList.length > 0 ? (
                                        filteredList.map((customer, index) => (
                                            <tr key={customer.id} className="hover:bg-blue-50 transition-colors">
                                                <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-600 font-bold">{index + 1}</td>
                                                <td className="p-2 sm:p-3 font-medium text-gray-800 text-sm sm:text-base">
                                                    <div className="truncate max-w-32 sm:max-w-none">{customer.name}</div>
                                                    <div className="sm:hidden text-xs text-gray-500 mt-1">
                                                        {activeFilter === 'financeScheme' ?
                                                            (customer.financeScheme?.financeCompanyName || 'N/A') :
                                                            (LOAN_TYPE_NAMES[customer.loanType] || customer.loanType)
                                                        }
                                                    </div>
                                                </td>
                                                {activeFilter === 'financeScheme' ? (
                                                    <>
                                                        <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-600">
                                                            {customer.financeScheme?.financeCompanyName || 'N/A'}
                                                        </td>
                                                        <td className="p-2 sm:p-3 text-xs sm:text-sm font-semibold text-indigo-600">
                                                            {formatCurrency(customer.financeScheme?.actualDisbursement || customer.financeScheme?.loanAmount || 0)}
                                                        </td>
                                                        <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-500">
                                                            {formatDate(customer.financeScheme?.createdAt || customer.createdAt)}
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-600 hidden sm:table-cell">
                                                            {LOAN_TYPE_NAMES[customer.loanType] || customer.loanType}
                                                        </td>
                                                        <td className="p-2 sm:p-3">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold
                                                            ${customer.status?.includes('Sanction') ? 'bg-green-100 text-green-700' :
                                                                    customer.status?.includes('Disburs') ? 'bg-purple-100 text-purple-700' :
                                                                        customer.status?.includes('Reject') ? 'bg-red-100 text-red-700' :
                                                                            'bg-yellow-100 text-yellow-700'}`}>
                                                                <span className="hidden sm:inline">{customer.status || 'Pending'}</span>
                                                                <span className="sm:hidden">
                                                                    {customer.status?.includes('Sanction') ? '✅' :
                                                                        customer.status?.includes('Disburs') ? '💰' :
                                                                            customer.status?.includes('Reject') ? '❌' : '⏳'}
                                                                </span>
                                                            </span>
                                                        </td>
                                                        <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-600 truncate max-w-[150px]" title={customer.remark}>
                                                            {customer.remark || '-'}
                                                        </td>
                                                        {activeFilter === 'disbursed' && (
                                                            <td className="p-2 sm:p-3 text-xs sm:text-sm font-semibold text-green-700 hidden sm:table-cell whitespace-nowrap">
                                                                {customer.financeScheme?.actualDisbursement
                                                                    ? formatCurrency(customer.financeScheme.actualDisbursement)
                                                                    : customer.loanAmount
                                                                        ? formatCurrency(customer.loanAmount)
                                                                        : '-'}
                                                            </td>
                                                        )}
                                                        <td className="p-2 sm:p-3 text-xs sm:text-sm text-gray-500 hidden md:table-cell whitespace-nowrap">
                                                            {activeFilter === 'disbursed' && customer.loanDisbursementDate
                                                                ? formatDate(customer.loanDisbursementDate)
                                                                : formatDate(customer.createdAt)}
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="p-4 sm:p-6 text-center text-gray-500 text-sm sm:text-base">
                                                No records found for this category
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )
            }

            {/* Analytics Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 mt-8">
                {/* Loan Counts Donut Chart */}
                <div className="bg-white p-6 rounded-2xl shadow-lg">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-gray-800">📈 Loan Category Breakdown</h3>
                        <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-sm font-bold border border-blue-100">
                            Total: {Object.values(dashboardData.loanCounts).reduce((a, b) => a + b, 0)}
                        </span>
                    </div>
                    <div className="h-96 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'Vehicle', value: dashboardData.loanCounts.carLoan, color: '#3B82F6' },
                                        { name: 'Home', value: dashboardData.loanCounts.homeLoan, color: '#10B981' },
                                        { name: 'LAP', value: dashboardData.loanCounts.lapLoan, color: '#8B5CF6' },
                                        { name: 'Personal', value: dashboardData.loanCounts.personalLoan, color: '#F59E0B' },
                                        { name: 'Business', value: dashboardData.loanCounts.businessLoan, color: '#06B6D4' },
                                        { name: 'Working Capital', value: dashboardData.loanCounts.workingCapitalLoan, color: '#EC4899' },
                                        { name: 'Agriculture', value: dashboardData.loanCounts.agricultureLoan, color: '#14B8A6' }
                                    ].filter(item => item.value > 0)}
                                    cx="50%"
                                    cy="45%"
                                    innerRadius={80}
                                    outerRadius={130}
                                    paddingAngle={3}
                                    dataKey="value"
                                >
                                    {[
                                        { name: 'Vehicle', value: dashboardData.loanCounts.carLoan, color: '#3B82F6' },
                                        { name: 'Home', value: dashboardData.loanCounts.homeLoan, color: '#10B981' },
                                        { name: 'LAP', value: dashboardData.loanCounts.lapLoan, color: '#8B5CF6' },
                                        { name: 'Personal', value: dashboardData.loanCounts.personalLoan, color: '#F59E0B' },
                                        { name: 'Business', value: dashboardData.loanCounts.businessLoan, color: '#06B6D4' },
                                        { name: 'Working Capital', value: dashboardData.loanCounts.workingCapitalLoan, color: '#EC4899' },
                                        { name: 'Agriculture', value: dashboardData.loanCounts.agricultureLoan, color: '#14B8A6' }
                                    ].filter(item => item.value > 0).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={2} stroke="#fff" />
                                    ))}
                                </Pie>
                                <RechartsTooltip
                                    contentStyle={{
                                        borderRadius: '12px',
                                        border: 'none',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                        padding: '12px'
                                    }}
                                />
                                <Legend
                                    iconType="circle"
                                    layout="horizontal"
                                    verticalAlign="bottom"
                                    align="center"
                                    wrapperStyle={{ paddingTop: '30px' }}
                                    formatter={(value, entry) => `${value}: ${entry.payload.value}`}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Status Funnel Chart */}
                <div className="bg-white p-6 rounded-2xl shadow-lg">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">📊 Status Funnel - Conversion Flow</h3>
                    <div className="h-96 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={dashboardData.chartData?.funnelData || []}
                                layout="vertical"
                                margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 14, fill: '#374151', fontWeight: 600 }}
                                    width={90}
                                />
                                <RechartsTooltip
                                    cursor={{ fill: '#F3F4F6' }}
                                    contentStyle={{
                                        borderRadius: '12px',
                                        border: 'none',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                        padding: '12px'
                                    }}
                                />
                                <Bar
                                    dataKey="value"
                                    radius={[0, 8, 8, 0]}
                                    label={{ position: 'right', fill: '#374151', fontSize: 14, fontWeight: 'bold' }}
                                >
                                    {(dashboardData.chartData?.funnelData || []).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Date-wise Daily Data Table */}
            <div className="bg-white rounded-2xl shadow-lg mb-8 overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center">
                        <span className="text-xl mr-2">📅</span>
                        Date-wise Daily Data
                    </h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-full">
                        <thead>
                            <tr className="bg-gray-100 text-gray-600 uppercase text-xs">
                                <th className="p-3 sm:p-4">Date</th>
                                <th className="p-3 sm:p-4 text-center">New Customers</th>
                                <th className="p-3 sm:p-4 text-center">Processing</th>
                                <th className="p-3 sm:p-4 text-center">Sanctioned</th>
                                <th className="p-3 sm:p-4 text-center">Rejected</th>
                                <th className="p-3 sm:p-4 text-center">Disbursed</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {(dashboardData.chartData?.dailyTrendData || []).length > 0 ? (
                                dashboardData.chartData.dailyTrendData.map((day, index) => (
                                    <tr key={index} className="hover:bg-blue-50 transition-colors">
                                        <td className="p-3 sm:p-4 font-semibold text-gray-800 text-sm">
                                            {new Date(day.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </td>
                                        <td className="p-3 sm:p-4 text-center text-sm font-medium text-blue-600">{day.leads}</td>
                                        <td className="p-3 sm:p-4 text-center text-sm font-medium text-yellow-600">{day.processing}</td>
                                        <td className="p-3 sm:p-4 text-center text-sm font-medium text-green-600">{day.sanctioned}</td>
                                        <td className="p-3 sm:p-4 text-center text-sm font-medium text-red-600">{day.rejected}</td>
                                        <td className="p-3 sm:p-4 text-center text-sm font-medium text-purple-600">{day.disbursed}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="p-4 sm:p-6 text-center text-gray-500 text-sm">
                                        No daily data found for the selected date range.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

        </div >
    );
};

export default AdminDashboard;
